/**
 * Staff Slice
 * Manages staff state and async operations
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { staffApi } from '../../utils/api/staff.js';
import { extractPaginatedList, normalizePagination } from '../../utils/pagination.js';
import { getErrorMessage } from '../../utils/api/errors.js';

/**
 * Initial state
 */
const initialState = {
  staff: [],
  currentStaff: null,
  stats: null,
  loading: false,
  listLoading: false,
  detailLoading: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    status: null,
    role: null,
    search: '',
  },
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

/**
 * Async thunk: Fetch all staff
 */
export const fetchStaff = createAsyncThunk(
  'staff/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await staffApi.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch staff'));
    }
  }
);

/**
 * Async thunk: Fetch staff by ID
 */
export const fetchStaffById = createAsyncThunk(
  'staff/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await staffApi.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch staff'));
    }
  }
);

/**
 * Async thunk: Create staff
 */
export const createStaff = createAsyncThunk(
  'staff/create',
  async (staffData, { rejectWithValue }) => {
    try {
      const response = await staffApi.create(staffData);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create staff'));
    }
  }
);

/**
 * Async thunk: Update staff
 */
export const updateStaff = createAsyncThunk(
  'staff/update',
  async ({ id, staffData }, { rejectWithValue }) => {
    try {
      const response = await staffApi.update(id, staffData);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update staff'));
    }
  }
);

/**
 * Async thunk: Delete staff
 */
export const deleteStaff = createAsyncThunk(
  'staff/delete',
  async (id, { rejectWithValue }) => {
    try {
      await staffApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete staff'));
    }
  }
);

/**
 * Async thunk: Toggle staff status
 */
export const toggleStaffStatus = createAsyncThunk(
  'staff/toggleStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await staffApi.updateStatus(id, status);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update staff status'));
    }
  }
);

/**
 * Async thunk: Fetch staff statistics
 */
export const fetchStaffStats = createAsyncThunk(
  'staff/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await staffApi.getStats();
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch statistics'));
    }
  }
);

/**
 * Staff slice
 */
const staffSlice = createSlice({
  name: 'staff',
  initialState,
  reducers: {
    /**
     * Clear current staff
     */
    clearCurrentStaff: (state) => {
      state.currentStaff = null;
      state.error = null;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set filters
     */
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Reset filters
     */
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    /**
     * Set pagination
     */
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all staff
      .addCase(fetchStaff.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchStaff.fulfilled, (state, action) => {
        state.listLoading = false;
        const { data, pagination } = extractPaginatedList(action.payload);
        state.staff = data;
        if (pagination) {
          state.pagination = normalizePagination(pagination, state.pagination);
        }
      })
      .addCase(fetchStaff.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload;
        state.staff = [];
      })

      // Fetch staff by ID
      .addCase(fetchStaffById.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchStaffById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentStaff = action.payload;
      })
      .addCase(fetchStaffById.rejected, (state) => {
        state.detailLoading = false;
        state.currentStaff = null;
      })

      // Create staff
      .addCase(createStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.staff.unshift(action.payload);
      })
      .addCase(createStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update staff
      .addCase(updateStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStaff.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.staff.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.staff[index] = action.payload;
        }
        if (state.currentStaff?.id === action.payload.id) {
          state.currentStaff = action.payload;
        }
      })
      .addCase(updateStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete staff
      .addCase(deleteStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.staff = state.staff.filter((s) => s.id !== action.payload);
        if (state.currentStaff?.id === action.payload) {
          state.currentStaff = null;
        }
      })
      .addCase(deleteStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Toggle staff status
      .addCase(toggleStaffStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleStaffStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.staff.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.staff[index] = action.payload;
        }
        if (state.currentStaff?.id === action.payload.id) {
          state.currentStaff = action.payload;
        }
      })
      .addCase(toggleStaffStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch statistics
      .addCase(fetchStaffStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaffStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchStaffStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearCurrentStaff,
  clearError,
  setFilters,
  resetFilters,
  setPagination,
} = staffSlice.actions;

export default staffSlice.reducer;
